<template>
    <form data-test="form:contact-company-update" @submit.prevent="onFormSubmit" @keyup.esc="cancel">
        <!-- eslint-disable vue/no-v-html -->
        <info-box
            v-if="formErrorMessage"
            type="negative"
            data-test="error:global-message"
            v-html="formErrorMessage"
        />
        <!-- eslint-enable -->

        <div class="row q-col-gutter-md">
            <div class="col-xs-12">
                <form-builder item-key="companyName" autofocus />
            </div>
            <div class="col-xs-6 col-sm-8 col-md-6 col-lg-5 col-xl-4">
                <form-builder item-key="uid" />
            </div>
            <div class="col-xs-6 col-sm-4 col-md-6 col-lg-3 col-xl-2">
                <form-builder item-key="isLiableToVat" />
            </div>
            <div class="col-xs-12 col-sm-12 col-md-12 col-lg-4 col-xl-3">
                <form-builder item-key="correspondenceLanguageId" />
            </div>
            <div class="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-xl-3">
                <form-builder item-key="isProfessionalPolicyholder" />
            </div>
        </div>

        <div class="row q-col-gutter-md q-pt-md">
            <div class="col-xs-12 col-sm-12 col-md-12 col-lg-8 col-xl-12">
                <form-builder item-key="website" />
            </div>
        </div>

        <div class="row q-col-gutter-md q-pt-md">
            <div class="col-xs-12 col-sm-4 col-md-12 col-lg-4 col-xl-3">
                <form-builder item-key="customerStatus" />
            </div>
            <div class="col-xs-6 col-sm-4 col-md-6 col-lg-4 col-xl-3">
                <form-builder item-key="customerSince" />
            </div>
            <div class="col-xs-6 col-sm-4 col-md-6 col-lg-4 col-xl-3">
                <form-builder item-key="customerUntil" :disable="formData.customerStatus !== CustomerStatus.INACTIVE" />
                <q-tooltip v-if="formData.customerStatus !== CustomerStatus.INACTIVE" :delay="1000" :offset="[0, 10]">{{ $t('common.contact.customer-until--disabled-info') }}</q-tooltip>
            </div>
            <div class="col-xs-12 col-lg-6">
                <form-builder item-key="consultants" :inject-consultants="contact.inactiveConsultants" />
            </div>
        </div>

        <form-dialog
            ref="customerUntilDialog"
            @confirm="resetCustomerUntilValue"
            @cancel="restoreCustomerStatusValue"
        >
            <template v-slot:title>{{ $tc('common.contact.customer-status.status-change', 1) }}</template>
            <template v-slot:default>
                <p>{{ $t('common.contact.customer-status.status-change--reset-customer-until-date-info', { date: contact.formattedCustomerUntil }) }}</p>
            </template>
        </form-dialog>

        <div class="row q-col-gutter-md q-pt-md">
            <div class="col-xs-12">
                <base-button
                    data-test="btn:cancel"
                    :label="$t('common.term.cancel')"
                    :disable="formSubmitStatus"
                    class="q-mr-sm"
                    flat
                    primary-button
                    @click="$emit('cancel')"
                />
                <base-button
                    data-test="btn:submit"
                    type="submit"
                    :label="$t('common.term.save')"
                    :disable="formSubmitStatus"
                    :loading="formSubmitStatus"
                    primary-button
                />
            </div>
        </div>
    </form>
</template>

<script>
import { formBuilderMixin } from '@/mixins/formBuilderMixin'
import { CustomerStatus } from '@/enums/graphql'
import { lightFormat } from 'date-fns'

import CONTACT_FIELDS from '@/forms/contact.fields.json'
import CONTACT_COMPANY_FIELDS from '@/forms/contact.company.fields.json'

import FormDialog from '@/components/form/FormDialog'

export default {
    name: 'ContactCompanyUpdateForm',
    components: {
        FormDialog,
    },
    mixins: [formBuilderMixin],
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    formBuilderSettings: {
        schema: {
            type: 'root',
            children: [
                ...CONTACT_FIELDS.children,
                ...CONTACT_COMPANY_FIELDS.children,
            ],
        },
    },
    validations () {
        return {
            formData: {
                customerUntil: {
                    required: (value) => {
                        if (this.formData.customerStatus === CustomerStatus.INACTIVE && !value) return false
                        return true
                    },
                },
            },
        }
    },
    data () {
        const customerUntil = !this.contact.customer_until
            ? this.contact.customer_status === CustomerStatus.INACTIVE
                ? lightFormat(new Date(), 'yyyy-MM-dd')
                : ''
            : this.contact.customer_until

        return {
            CustomerStatus,
            customerUntil: customerUntil,
            formData: {
                companyName: this.contact.company_name,
                uid: this.contact.uid,
                isLiableToVat: this.contact.is_liable_to_vat,
                isProfessionalPolicyholder: this.contact.is_professional_policyholder,
                website: this.contact.website,
                customerStatus: this.contact.customer_status,
                customerSince: this.contact.customer_since || '',
                consultants: this.contact.consultants.map(consultant => consultant.id),
                correspondenceLanguageId: this.contact.correspondenceLanguage.id,
                customerUntil: customerUntil,
            },
        }
    },
    watch: {
        'formData.customerStatus' (newVal, oldVal) {
            if (newVal === CustomerStatus.INACTIVE) this.updateCustomerUntilValue()
            if (oldVal === CustomerStatus.INACTIVE && newVal !== CustomerStatus.INACTIVE) {
                if (this.contact.customer_until) {
                    this.$refs.customerUntilDialog.open()
                } else {
                    this.resetCustomerUntilValue()
                }
            }
        },
        'formData.customerUntil' (newVal) {
            if (!newVal) return
            this.customerUntil = newVal
        },
    },
    methods: {
        handleFormData (formData) {
            this.contact.update(formData)
                .then(company => {
                    this.$emit('update', company)
                    this.$emit('done')
                    this.resetFormSubmitStatus()
                    this.$q.notify({
                        type: 'positive',
                        message: this.$tc('common.notifications.contact.contact-saved-success', 1),
                    })
                    this.customerUntil = null
                })
                .catch(error => {
                    this.showFormErrorMessage(error)
                    this.resetFormSubmitStatus()
                    this.$q.notify({
                        type: 'negative',
                        message: this.$tc('common.term.error', 1),
                        caption: this.$tc('common.notifications.contact.contact-saved-error', 1),
                    })
                })
        },
        cancel () {
            this.$emit('cancel')
        },
        updateCustomerUntilValue () {
            if (!this.customerUntil) this.customerUntil = lightFormat(new Date(), 'yyyy-MM-dd')
            this.formData.customerUntil = this.customerUntil
        },
        resetCustomerUntilValue () {
            this.formData.customerUntil = ''
        },
        restoreCustomerStatusValue () {
            this.formData.customerStatus = CustomerStatus.INACTIVE
        },
    },
}
</script>
